// Phase I4 — page-level chrome bits (CTA, TierToggle, DirectionToggle).
// Extracted from DailyAccaPage.tsx to keep that file under the 250-line
// target. Pure presentational; no data fetching here.

import { Link } from "react-router-dom";

export type Direction = "A" | "B" | "C";

const fontMono = "var(--sa-font-mono)";

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: "8px 16px",
  background: active ? "var(--sa-accent-saffron)" : "transparent",
  color: active ? "#0A0A0A" : "var(--sa-fg-secondary)",
  border: "none",
  fontFamily: fontMono,
  fontSize: 11,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  cursor: "pointer",
  fontWeight: 500,
});

export function DirectionToggle({
  value,
  onChange,
}: {
  value: Direction;
  onChange: (d: Direction) => void;
}): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label="Daily Acca layout"
      style={{ display: "inline-flex", border: "1px solid var(--sa-hairline)", borderRadius: 4 }}
    >
      {(["A", "B", "C"] as Direction[]).map((d) => (
        <button
          key={d}
          role="tab"
          aria-selected={d === value}
          onClick={() => onChange(d)}
          style={tabStyle(d === value)}
        >
          LAYOUT {d}
        </button>
      ))}
    </div>
  );
}

const tierTab = (active: boolean, accent?: boolean): React.CSSProperties => ({
  padding: "8px 16px",
  background: active ? "var(--sa-bg-raised)" : "transparent",
  color: accent ? "var(--sa-accent-saffron)" : active ? "var(--sa-fg-primary)" : "var(--sa-fg-muted)",
  border: "none",
  fontFamily: fontMono,
  fontSize: 11,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  fontWeight: 500,
  textDecoration: "none",
});

export function TierToggle({
  effectiveTier,
  authenticated,
}: {
  effectiveTier: "free" | "pro";
  authenticated: boolean;
}): JSX.Element {
  const proHref = authenticated ? "/pricing" : "/auth?intent=signup";
  return (
    <div style={{ display: "inline-flex", border: "1px solid var(--sa-hairline)", borderRadius: 4 }}>
      <span style={tierTab(effectiveTier === "free")}>FREE</span>
      {effectiveTier === "pro" ? (
        <span style={tierTab(true, true)}>PRO</span>
      ) : (
        <Link to={proHref} style={tierTab(false)}>PRO →</Link>
      )}
    </div>
  );
}

const ctaPrimary: React.CSSProperties = {
  background: "var(--sa-accent-saffron)",
  color: "#0A0A0A",
  padding: "13px 22px",
  fontFamily: fontMono,
  fontSize: 11.5,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  fontWeight: 500,
  borderRadius: 4,
  textDecoration: "none",
};

const ctaGhost: React.CSSProperties = {
  ...ctaPrimary,
  background: "transparent",
  color: "var(--sa-fg-primary)",
  border: "1px solid var(--sa-hairline-bright)",
};

export function UpgradeCta(): JSX.Element {
  return (
    <section style={{ padding: "32px", borderTop: "1px solid var(--sa-hairline)" }}>
      <div
        style={{
          border: "1px solid var(--sa-amber-dim)",
          background: "var(--sa-bg-panel)",
          borderRadius: 8,
          padding: 28,
          maxWidth: 1180,
          margin: "0 auto",
        }}
      >
        <h2 style={{ fontFamily: "var(--sa-font-display)", fontSize: 28, fontWeight: 700, margin: 0 }}>
          Want the full intelligence stack?
        </h2>
        <p style={{ fontFamily: "var(--sa-font-body)", fontSize: 14, color: "var(--sa-fg-secondary)", lineHeight: 1.7, marginTop: 12 }}>
          Paid members see <strong>Super Acca</strong> (single-match Bet Builder, 4×–15×),
          Correct Score insights with model edge %, Player DNA goalscorer streaks, and the full
          agent debate behind every pick.
        </p>
        <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
          <Link to="/auth?intent=signup" style={ctaPrimary}>CREATE FREE ACCOUNT</Link>
          <Link to="/" style={ctaGhost}>SEE HOW IT WORKS</Link>
        </div>
      </div>
    </section>
  );
}
